import filter from 'lodash/filter';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompact';
import { defaultMemoize } from 'reselect';

import { errorBoundary } from 'src/app/hocs';
import {
  getCartItems,
  getNumPendingTransfers,
  getOptimisticPendingTransfers,
  getPatients,
  getUnarchivedMedications,
} from 'src/app/state/selectors';
import type { RootState } from 'src/app/state/store';
import type { HomeItem, OptimisticPendingTransfer } from 'src/app/types';
import { HomeProductStatus } from 'src/consts';

import Renderer from '.';

function makeMapStateToProps() {
  const hasMedicationsReadyToCheckout = defaultMemoize(homescreenMedications => {
    const pendingTransfers = filter(homescreenMedications, {
      items: [{ status: HomeProductStatus.READY_TO_CHECKOUT }],
    });
    return pendingTransfers.length;
  });

  const removePendingMedicationsFromHomescreenItems = defaultMemoize((homescreenMedications: HomeItem[]) => {
    return homescreenMedications?.map(({ patient, items }) => {
      const filteredItems = items?.filter(({ status, type }) => {
        const isItemPending = status === 'pending' && type === 'transfer';
        return !isItemPending;
      });
      return { patient, items: filteredItems };
    });
  });

  const getActiveMedicationsCount = defaultMemoize((homescreenMedications: HomeItem[]) => {
    return homescreenMedications?.reduce((currentCount, { items }) => {
      return currentCount + items.length;
    }, 0);
  });

  return (state: RootState) => {
    const cartItems = getCartItems(state);
    const homescreenMedications = getUnarchivedMedications(state);
    const numPendingMeds = getNumPendingTransfers(state);

    // Ensuring that each medication is in the right section by filtering for is_hidden (and expiration time for archived) respectively.
    //  if transfer pending med feature is on, do not return pending transfers
    const filteredHomescreenMedications = removePendingMedicationsFromHomescreenItems(homescreenMedications ?? []);

    const activeHomescreenMedicationsCount = getActiveMedicationsCount(filteredHomescreenMedications);

    const totalQuantity = cartItems?.reduce((tmpTotalQuantity, cartItem) => {
      if (cartItem.otcItemId) {
        tmpTotalQuantity += cartItem.quantity;
      } else {
        tmpTotalQuantity += 1;
      }
      return tmpTotalQuantity;
    }, 0);

    const hasCart = totalQuantity > 0;

    const allPatients = getPatients(state);

    const showAddRxButton = !!numPendingMeds;
    const showSecondaryStyle = !!hasMedicationsReadyToCheckout(homescreenMedications);

    return {
      activeHomescreenMedicationsCount,
      allPatients,
      filteredHomescreenMedications,
      hasCart,
      // homescreenMedications is grouped by patient so if there is more than one entry, there is multiple patients
      hasMultiplePatients: (homescreenMedications?.length ?? 0) > 1,
      numPendingMeds,
      optimisticPendingTransfers: getOptimisticPendingTransfers(state) as OptimisticPendingTransfer[],
      showAddRxButton,
      showSecondaryStyle,
      totalQuantity,
    };
  };
}

const connector = connect(makeMapStateToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

export default compose(setDisplayName('Medications'), errorBoundary, connector)(Renderer);
